import { ReactNode } from 'react';
import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts';
import {
  ArrowForwardRounded,
  AssignmentIndOutlined,
  LockOutlined,
  CloudOffOutlined,
  PersonAddAltOutlined,
  TrendingDownRounded,
  TrendingFlatRounded,
  TrendingUpRounded,
  WorkOutlineOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Link } from 'react-router-dom';

import UserAvatar from 'components/user/avatar';
import { PageHeader } from 'components/layout/PageHeader';
import { EmptyState } from 'components/empty.state';
import { useGetDashboard } from 'core/dashboard/hooks';
import { GetDashboardResponse } from 'core/dashboard/api';
import { useUserSettingsStateContext } from 'core/user.settings/hooks';

dayjs.extend(relativeTime);

export const HAIRLINE = '#EDF0F3';
const CHART_BLUE = '#2E6BEB';
const CHART_GREEN = '#33B038';

type DashboardPageProps = {
  queryKey: string;
  url: string;
  title: string;
  subtitle?: string;
  /** Extra sections rendered above the KPI cards (e.g. network totals). */
  renderTop?: (data: GetDashboardResponse) => ReactNode;
  /** Extra sections rendered between the charts and the activity panels. */
  renderMiddle?: (data: GetDashboardResponse) => ReactNode;
};

/* ------------------------------------------------------------------ */
/* Building blocks                                                     */
/* ------------------------------------------------------------------ */

/** White rounded card with a hairline border — the dashboard's base surface. */
export const Panel = ({ children, sx }: { children: ReactNode; sx?: object }) => (
  <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${HAIRLINE}`, borderRadius: '16px', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', ...sx }}>
    {children}
  </Box>
);

export const PanelHeader = ({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) => (
  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} sx={{ px: 3, pt: 2.5, pb: 2 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>}
    </Box>
    {action}
  </Stack>
);

export const SeeAllLink = ({ to }: { to: string }) => (
  <Box
    component={Link}
    to={to}
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
      fontSize: '0.8125rem', fontWeight: 600, color: 'secondary.main', textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' },
    }}
  >
    Voir tout <ArrowForwardRounded sx={{ fontSize: '1rem' }} />
  </Box>
);

const STAT_STYLES: Record<string, { icon: ReactNode; color: string; bg: string }> = {
  'Nouveaux inscrits': { icon: <PersonAddAltOutlined />, color: CHART_BLUE, bg: '#ECF2FF' },
  'Nouvelles missions': { icon: <WorkOutlineOutlined />, color: '#1CC266', bg: '#E5FFF2' },
  'Nouvelles candidatures': { icon: <AssignmentIndOutlined />, color: '#FF8D3A', bg: '#FFF8EF' },
};

const DashboardStat = ({ title, value, diff }: { title: string; value: number; diff: number }) => {
  const style = STAT_STYLES[title] ?? { icon: <TrendingUpRounded />, color: CHART_BLUE, bg: '#ECF2FF' };
  const trend = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const trendColor = trend === 'up' ? 'success.main' : trend === 'down' ? 'error.main' : 'text.secondary';
  const trendBg = trend === 'up' ? 'success.light' : trend === 'down' ? 'error.light' : '#F1F3F6';
  const TrendIcon = trend === 'up' ? TrendingUpRounded : trend === 'down' ? TrendingDownRounded : TrendingFlatRounded;

  return (
    <Panel sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.secondary' }}>{title}</Typography>
        <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: style.bg, color: style.color, flexShrink: 0, '& svg': { fontSize: '1.375rem' } }}>
          {style.icon}
        </Box>
      </Stack>
      <Box>
        <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.03em', color: 'text.primary' }}>{value}</Typography>
        <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, height: 22, borderRadius: '11px', bgcolor: trendBg, color: trendColor, fontSize: '0.75rem', fontWeight: 700 }}>
            <TrendIcon sx={{ fontSize: '0.9375rem' }} />
            {diff > 0 ? `+${diff}` : diff}
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>vs semaine précédente · 7 derniers jours</Typography>
        </Stack>
      </Box>
    </Panel>
  );
};

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Typography>
  </Stack>
);

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const monthLabel = (value: number) => dayjs().subtract(12 - value, 'month').format('MMM YY');

const chartSx = {
  '& .MuiAreaElement-root': { fillOpacity: 0.08 },
  '& .MuiLineElement-root': { strokeWidth: 2 },
  '& .MuiChartsAxis-tickLabel': { fill: '#868F99', fontSize: '0.6875rem' },
  '& .MuiChartsAxis-line': { stroke: HAIRLINE },
  '& .MuiChartsGrid-line': { stroke: HAIRLINE, strokeDasharray: '3 3' },
};

type TrendChartProps = {
  title: string;
  subtitle: string;
  series: { data: number[] | undefined; label: string; color: string; valueFormatter?: (value: number | null) => string }[];
};

const TrendChart = ({ title, subtitle, series }: TrendChartProps) => (
  <Panel sx={{ minWidth: 0 }}>
    <PanelHeader
      title={title}
      subtitle={subtitle}
      action={<Stack direction="row" gap={2} sx={{ pt: 0.25 }}>{series.map((s) => <LegendDot key={s.label} color={s.color} label={s.label} />)}</Stack>}
    />
    <Box sx={{ px: 1, pb: 1 }}>
      <LineChart
        xAxis={[{ data: MONTHS, valueFormatter: monthLabel, disableTicks: true, disableLine: true, scaleType: 'point' }]}
        yAxis={[{ disableLine: true, disableTicks: true, tickMinStep: 1 }]}
        grid={{ horizontal: true, vertical: false }}
        series={series.map((s) => ({ data: s.data ?? [], label: s.label, color: s.color, area: true, showMark: false, curve: 'monotoneX', valueFormatter: s.valueFormatter }))}
        slotProps={{ legend: { hidden: true } }}
        margin={{ top: 16, right: 24, bottom: 32, left: 40 }}
        height={260}
        sx={chartSx}
      />
    </Box>
  </Panel>
);

const ActivityRow = ({ to, avatar, primary, secondary, chips }: { to: string; avatar: ReactNode; primary: ReactNode; secondary: string; chips?: ReactNode }) => (
  <Box
    component={Link}
    to={to}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, textDecoration: 'none', color: 'inherit',
      borderTop: `1px solid ${HAIRLINE}`, transition: 'background-color 120ms ease',
      '&:hover': { bgcolor: '#F7F8FA' },
    }}
  >
    {avatar}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>{primary}</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{secondary}</Typography>
    </Box>
    {chips && <Stack direction="row" gap={0.75} sx={{ flexShrink: 0 }}>{chips}</Stack>}
  </Box>
);

/* ------------------------------------------------------------------ */
/* Skeleton                                                            */
/* ------------------------------------------------------------------ */

const SkeletonPage = () => (
  <Stack gap={3} aria-busy="true" aria-label="Chargement">
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
      {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={148} sx={{ borderRadius: '16px', bgcolor: '#EEF1F5' }} />)}
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
      {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={340} sx={{ borderRadius: '16px', bgcolor: '#EEF1F5' }} />)}
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
      {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={320} sx={{ borderRadius: '16px', bgcolor: '#EEF1F5' }} />)}
    </Box>
  </Stack>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const DashboardPage = ({ queryKey, url, title, subtitle, renderTop, renderMiddle }: DashboardPageProps) => {
  const { data, isLoading, isError, error } = useGetDashboard({ queryKey, url });
  const status = (error as any)?.response?.status as number | undefined;
  const userSettingsState = useUserSettingsStateContext();
  const { first_name } = JSON.parse(userSettingsState.user || '{}');

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle ?? `Bonjour ${first_name} ! 🙂`}
      />
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1440, mx: 'auto' }}>
        {isLoading && <SkeletonPage />}
        {!isLoading && (isError || !data) && (
          <Panel>
            {status === 403 ? (
              <EmptyState size="md" icon={<LockOutlined />} title="Accès non autorisé" description="Cette vue est réservée aux administrateurs. Si vous pensez qu'il s'agit d'une erreur, contactez un administrateur Chronos." />
            ) : (
              <EmptyState size="md" icon={<CloudOffOutlined />} title="Impossible de charger les données" description={status ? `Le serveur a répondu avec une erreur (${status}). Réessayez dans quelques instants.` : 'Vérifiez votre connexion puis réessayez.'} />
            )}
          </Panel>
        )}
        {!isLoading && data && (
          <Stack gap={3}>
            {renderTop?.(data)}

            {/* KPIs */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 3 }}>
              {data.stats.map((stat) => (
                <DashboardStat key={stat.title} title={stat.title} value={stat.value} diff={stat.diff} />
              ))}
            </Box>

            {/* Trends */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
              <TrendChart
                title="Inscriptions et synchronisations"
                subtitle="12 derniers mois"
                series={[
                  { data: data.yearly_stats.new_candidates_count_per_month, label: 'Inscriptions', color: CHART_BLUE },
                  { data: data.yearly_stats.synced_candidates_count_per_month, label: 'Synchronisations', color: CHART_GREEN },
                ]}
              />
              <TrendChart
                title="Temps de traitement des candidatures"
                subtitle="Moyenne mensuelle, en jours"
                series={[
                  { data: data.yearly_stats.average_processing_time_per_month, label: 'Jours', color: CHART_BLUE, valueFormatter: (value) => (value == null ? '' : `${value} jours`) },
                ]}
              />
            </Box>

            {renderMiddle?.(data)}

            {/* Activity */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 3, alignItems: 'start' }}>
              <Panel sx={{ minWidth: 0, overflow: 'hidden' }}>
                <PanelHeader title="Inscriptions récentes" subtitle="Derniers candidats inscrits via l'application" action={<SeeAllLink to="/dashboard/registrations" />} />
                {data.last_candidates.length === 0 && (
                  <EmptyState icon={<PersonAddAltOutlined />} title="Aucune inscription récente" description="Les nouveaux candidats inscrits via l'application apparaîtront ici." />
                )}
                {data.last_candidates.map((candidate) => (
                  <ActivityRow
                    key={candidate.id}
                    to={`/dashboard/candidates/${candidate.id}`}
                    avatar={<UserAvatar user={candidate} />}
                    primary={candidate.full_name}
                    secondary={`Inscrit ${dayjs().to(dayjs(candidate.created_at))}`}
                    chips={candidate.has_offers ? <Chip label="A candidaté" color="info" /> : undefined}
                  />
                ))}
              </Panel>

              <Panel sx={{ minWidth: 0, overflow: 'hidden' }}>
                <PanelHeader title="Candidatures récentes" subtitle="Dernières candidatures reçues sur vos missions" action={<SeeAllLink to="/dashboard/offers" />} />
                {data.last_applications.length === 0 && (
                  <EmptyState icon={<AssignmentIndOutlined />} title="Aucune candidature récente" description="Les candidatures reçues sur vos missions apparaîtront ici." />
                )}
                {data.last_applications.map((application) => (
                  <ActivityRow
                    key={application.id}
                    to={`/dashboard/offers/${application.offer_id}`}
                    avatar={<UserAvatar user={application.candidate} />}
                    primary={<>{application.candidate.full_name} <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>→ {application.job_title}</Box></>}
                    secondary={dayjs().to(dayjs(application.created_at))}
                    chips={(
                      <>
                        <Chip label={`${application.applications_count} candidat${application.applications_count > 1 ? 's' : ''}`} color="warning" />
                        <Chip label={application.job_type} color="success" />
                      </>
                    )}
                  />
                ))}
              </Panel>
            </Box>
          </Stack>
        )}
      </Box>
    </>
  );
};

export default DashboardPage;
